import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAppServices } from "../hook/services";
import { Roles, TeamRoles } from "../enums";
import { clearSession, endSession, getToken, getUser, isTokenExpired, setSession } from "./storage";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

export const isAdmin = (user) => Boolean(user?.roles?.includes(Roles.ADMIN));

// Anyone on the super admin's team may sign in; what they can do depends on
// the role (see backend routes/team).
export const isTeam = (user) => Boolean(user?.roles?.some((r) => TeamRoles.includes(r)));
export const roleOf = (user) => TeamRoles.find((r) => user?.roles?.includes(r)) || null;

/**
 * Admin session state.
 *
 * The backend issues a 1-day JWT from POST /auth/login and returns the user
 * alongside it, so there is no "fetch me" endpoint to call on boot — the stored
 * user is the source of truth until a request comes back 401.
 */
export const AuthProvider = ({ children }) => {
  const { auth, team } = useAppServices();
  const [user, setUser] = useState(() => getUser());
  const [token, setToken] = useState(() => getToken());

  const login = useCallback(
    async ({ email, password }) => {
      const { response, error } = await auth.Login({ email, password });

      if (error) return { error: error?.message || "Could not sign you in." };

      // Login returns { token, ...user }. The backend spreads the raw Mongo
      // document, so strip the credential fields before anything is stored.
      const { token: issued, password: _pw, assign_password: _ap, ...account } = response || {};

      if (!issued) return { error: "The server did not return a session token." };
      if (!isTeam(account))
        return { error: "That account does not have admin access." };

      setSession(issued, account);
      setToken(issued);
      setUser(account);
      return { user: account };
    },
    [auth],
  );

  // Pages left open make no calls (the live support inbox, say), so a 401
  // alone can't be relied on: the token's own expiry is checked on load, every
  // minute, and whenever the tab comes back into focus.
  useEffect(() => {
    if (!token) return undefined;
    const check = () => {
      if (isTokenExpired(token)) endSession();
    };
    check();
    const timer = setInterval(check, 60 * 1000);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", check);
    };
  }, [token]);

  // The stored user is a snapshot from sign-in. Refresh the parts an admin can
  // change in the meantime — role, name, whether a new password is due — so a
  // promotion or demotion shows without signing out.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    team.Me().then(({ response }) => {
      if (!alive || !response) return;
      setUser((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          username: response.name || prev.username,
          roles: [...(prev.roles || []).filter((r) => !TeamRoles.includes(r)), ...(response.role ? [response.role] : [])],
          mustChangePassword: response.mustChangePassword,
        };
        setSession(token, next);
        return next;
      });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // After changing their own password the member gets a fresh token; the old
  // one stops working on the server.
  const replaceSession = useCallback(
    (nextToken, patch = {}) => {
      const next = { ...(user || {}), ...patch };
      setSession(nextToken, next);
      setToken(nextToken);
      setUser(next);
    },
    [user],
  );

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      replaceSession,
      isAuthenticated: Boolean(token && user),
      isTeam: isTeam(user),
      // Admin of the team: may manage it.
      isAdmin: isAdmin(user),
      role: roleOf(user),
      // Viewers see everything but change nothing; the backend enforces it.
      canEdit: isAdmin(user) || Boolean(user?.roles?.includes(Roles.MEMBER)),
    }),
    [user, token, login, logout, replaceSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
